import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Layout,
  Drawer,
  Button,
  Input,
  Tooltip,
  Typography,
  Card,
  Row,
  Col,
  Space,
  Tag,
  Avatar,
  Divider,
} from "antd";

import {
  DashboardOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ReadOutlined,
  FormOutlined,
  FileTextOutlined,
  BookOutlined,
  BarChartOutlined,
  FileAddOutlined,
  CalendarOutlined,
  MessageOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SolutionOutlined,
  RocketOutlined,
  BookFilled,
  EditOutlined,
  FileDoneOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import {
  FaChalkboardTeacher,
  FaClipboardList,
  FaGraduationCap,
  FaUserCheck,
  FaClipboardCheck,
  FaBookOpen,
  FaChartLine,
  FaTasks,
} from "react-icons/fa";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import TeacherAnalytics from "./TeacherAnalytics";
import ViewCurriculums from "../NewDashboard/LocalLessonPlan/ViewCurriculums";
import Darkmode from "../NewDashboard/Darkmode/Darkmode";
import LessonPlan from "../pages/Teachers/LessonPlan";
import ViewLessonPlan from "../pages/Teachers/ViewLessonPlan";
import Test from "../pages/Teachers/Test";
import TestPraint from "../pages/Teachers/TestPraint";
import ProfileImageTeacher from "../NewDashboard/ProfileImageTeacher";
import TestAttachment from "./TestAttachment";
import AttendanceRecord from "../pages/Attendance/AttendanceRecord";
import AttendanceData from "../pages/Attendance/AttendanceData";
import AssignmentView from "../pages/Teachers/AssignmentView";
import PostAssignment from "../pages/Teachers/PostAssignment";
import GradeDistribution from "../pages/Reportcard/GradeDistribution";
import NotificationIcon from "../components/ChatApp/NotificationIcon";
import Notice from "../components/ChatApp/Notice";
import Complaint from "./Complaint";
import PostNotes from "../pages/Teachers/PostNotes";

import CurriculumTeacherWorkspace from "../NewDashboard/CurriculumTeacherWorkspace";
import CurriculumTopicDetails from "../NewDashboard/CurriculumTopicDetails";
import CurriculumLessonEditor from "../NewDashboard/CurriculumLessonEditor";

import logo from "./zpa.png";

const {
  Header,
  Sider,
  Content,
} = Layout;

const {
  Title,
  Text,
} = Typography;

/* ============================================================
   TEACHER DASHBOARD
   OFFICIAL SCHOOL DASHBOARD VISUAL SYSTEM
   DEEP NAVY • BLACK • GOLD • WHITE
============================================================ */

const COLORS = {
  primary: "#0A2A43",
  primaryDark: "#071D2F",
  primaryDeep: "#04131F",
  secondary: "#123F63",

  page: "#F5F7FA",
  pureWhite: "#FFFFFF",
  text: "#111827",
  muted: "#667085",
  border: "#D9E0E7",
  soft: "#F8FAFC",

  success: "#198754",
  warning: "#C58A00",
  error: "#B42318",
  info: "#1D5F8C",

  sidebar: "#050B12",
  sidebarAlt: "#0A1724",
  sidebarBorder: "#1B3042",
  sidebarText: "#E8EEF3",
  sidebarMuted: "#91A4B3",
  sidebarIcon: "#9EB2C1",
  sidebarBright: "#F6C944",

  gold: "#F6C944",
  yellow: "#EAB308",
  orange: "#C58A00",
  green: "#198754",
  emerald: "#22A06B",
  sky: "#1D5F8C",

  black: "#050B12",
  white: "#F8FAFC",
  gray: "#A7B4BF",

  shadow:
    "0 12px 35px rgba(5,11,18,.10)",
};

/* ============================================================
   MENU ITEMS
============================================================ */

const menuItems = [
  {
    key: "quicklinks",
    icon: <DashboardOutlined />,
    label: "Dashboard",
  },

  {
    key: "gradedistribution",
    icon: <BarChartOutlined />,
    label: "Grades Input",
  },

  {
    key: "viewcurriculums",
    icon: <ReadOutlined />,
    label: "Curriculum Tools",
  },

  {
    key: "curriculumteacherworkspace",
    icon: <FaChalkboardTeacher />,
    label: "Teacher Workspace",
  },

  {
    key: "curriculumtopicdetails",
    icon: <BookOutlined />,
    label: "Topic Details",
  },

  {
    key: "curriculumlessoneditor",
    icon: <FormOutlined />,
    label: "Lesson Editor",
  },

  {
    key: "lessonplan",
    icon: <SolutionOutlined />,
    label: "Lesson Plan",
  },

  {
    key: "viewlessonplan",
    icon: <FileTextOutlined />,
    label: "View Lesson Plans",
  },

  {
    key: "assignmentview",
    icon: <FaClipboardList />,
    label: "Assignments",
  },

  {
    key: "test",
    icon: <FileDoneOutlined />,
    label: "Create Tests",
  },

  {
    key: "teachertests",
    icon: <FileTextOutlined />,
    label: "Teacher Tests",
  },

  {
    key: "complaint",
    icon: <MessageOutlined />,
    label: "Complaints",
  },
];

/* ============================================================
   NAVIGATION SECTIONS
============================================================ */

const navigationSections = [
  {
    key: "command",
    title: "COMMAND CENTER",
    items: [
      "quicklinks",
      "gradedistribution",
    ],
  },

  {
    key: "curriculum",
    title: "CURRICULUM & TEACHING",
    items: [
      "viewcurriculums",
      "curriculumteacherworkspace",
      "curriculumtopicdetails",
      "curriculumlessoneditor",
    ],
  },

  {
    key: "lesson-assessment",
    title: "LESSON & ASSESSMENT",
    items: [
      "lessonplan",
      "viewlessonplan",
      "assignmentview",
      "test",
      "teachertests",
    ],
  },

  {
    key: "communication",
    title: "COMMUNICATION",
    items: [
      "complaint",
    ],
  },
];

/* ============================================================
   TEACHER DASHBOARD
============================================================ */

const TeacherDashboard = () => {
  const [collapsed, setCollapsed] =
    useState(false);

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [darkMode, setDarkMode] =
    useState(false);

  const [activePage, setActivePage] =
    useState("quicklinks");

  const [visibleIndex, setVisibleIndex] =
    useState(0);

  const [openAttachment, setOpenAttachment] =
    useState(false);

  const [openNotes, setOpenNotes] =
    useState(false);

  const [menuSearch, setMenuSearch] =
    useState("");

  const [isNavigating, setIsNavigating] =
    useState(false);

  const [pendingPage, setPendingPage] =
    useState(null);

  const [windowWidth, setWindowWidth] =
    useState(
      typeof window !== "undefined"
        ? window.innerWidth
        : 1200
    );

  const isMobile =
    windowWidth < 900;

  const isTablet =
    windowWidth >= 900 &&
    windowWidth < 1200;

  /* ==========================================================
     RESPONSIVE
  ========================================================== */

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(
        window.innerWidth
      );

      if (window.innerWidth < 900) {
        setCollapsed(false);
      }
    };

    handleResize();

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);

  /* ==========================================================
     GLOBAL STYLES
  ========================================================== */

  useEffect(() => {
    const style =
      document.createElement("style");

    style.id =
      "teacher-official-dashboard-styles";

    style.innerHTML = `
      * {
        box-sizing: border-box;
      }

      html,
      body,
      #root {
        margin: 0;
        min-height: 100%;
      }

      body {
        overflow: hidden;
        background:
          linear-gradient(
            135deg,
            #F5F7FA 0%,
            #FFFFFF 50%,
            #F3F6F9 100%
          );
        color: #111827;
      }

      ::-webkit-scrollbar {
        width: 7px;
        height: 7px;
      }

      ::-webkit-scrollbar-track {
        background: transparent;
      }

      ::-webkit-scrollbar-thumb {
        background: #A7B4BF;
        border-radius: 20px;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: #0A2A43;
      }

      * {
        scrollbar-width: thin;
        scrollbar-color:
          #A7B4BF
          transparent;
      }

      /* ======================================================
         HEADER MENU BUTTON
      ====================================================== */

      .teacher-menu-button {
        width: 39px !important;
        height: 39px !important;
        min-width: 39px !important;
        padding: 0 !important;

        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        flex-shrink: 0 !important;

        border-radius: 9px !important;
        border:
          1px solid #D9E0E7 !important;

        background:
          #FFFFFF !important;

        color:
          #0A2A43 !important;

        box-shadow:
          none !important;

        transition:
          all 180ms ease !important;
      }

      .teacher-menu-button:hover {
        color:
          #0A2A43 !important;

        border-color:
          #D4A017 !important;

        background:
          #FFF9E6 !important;
      }

      .teacher-dark
      .teacher-menu-button {
        background:
          #101D29 !important;

        border-color:
          #263B4C !important;

        color:
          #F6C944 !important;
      }

      /* ======================================================
         HEADER ACTIONS
      ====================================================== */

      .teacher-header-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        flex-shrink: 0;
        min-width: max-content;
      }

      .teacher-header-action {
        width: 39px;
        height: 39px;
        min-width: 39px;
        padding: 0;

        border-radius: 9px;

        border:
          1px solid #D9E0E7;

        background:
          #FFFFFF;

        color:
          #294A62;

        display: flex;
        align-items: center;
        justify-content: center;

        cursor: pointer;

        transition:
          all 160ms ease;

        flex-shrink: 0;
      }

      .teacher-header-action:hover {
        color:
          #0A2A43;

        border-color:
          #D4A017;

        background:
          #FFF9E6;
      }

      .teacher-dark
      .teacher-header-action {
        background:
          #101D29;

        border-color:
          #263B4C;

        color:
          #E8EEF3;
      }

      /* ======================================================
         SIDEBAR SEARCH
      ====================================================== */

      .teacher-sidebar-search
      .ant-input-affix-wrapper {
        min-height: 39px !important;

        border-radius: 8px !important;

        background:
          #0A1724 !important;

        border:
          1px solid #1F3548 !important;

        box-shadow:
          none !important;
      }

      .teacher-sidebar-search
      .ant-input-affix-wrapper:hover {
        border-color:
          #A98525 !important;
      }

      .teacher-sidebar-search
      .ant-input {
        background:
          transparent !important;

        color:
          #EEF3F7 !important;
      }

      .teacher-sidebar-search
      .ant-input::placeholder {
        color:
          #718697 !important;
      }

      .teacher-sidebar-search
      .ant-input-affix-wrapper-focused {
        border-color:
          #F6C944 !important;

        box-shadow:
          0 0 0 2px
          rgba(246,201,68,.12) !important;
      }

      /* ======================================================
         SIDEBAR SCROLL
      ====================================================== */

      .teacher-sidebar-scroll {
        flex: 1;
        min-height: 0;

        overflow-y: auto;
        overflow-x: hidden;

        padding-bottom: 28px;

        scrollbar-width: thin;

        scrollbar-color:
          rgba(246,201,68,.50)
          transparent;
      }

      .teacher-sidebar-scroll::-webkit-scrollbar {
        width: 6px;
      }

      .teacher-sidebar-scroll::-webkit-scrollbar-track {
        background:
          transparent;
      }

      .teacher-sidebar-scroll::-webkit-scrollbar-thumb {
        background:
          linear-gradient(
            180deg,
            rgba(246,201,68,.75),
            rgba(185,154,82,.50)
          );

        border-radius:
          20px;
      }

      .teacher-sidebar-scroll::-webkit-scrollbar-thumb:hover {
        background:
          linear-gradient(
            180deg,
            #F6C944,
            #B99A52
          );
      }

      /* ======================================================
         SIDEBAR SECTION TITLES
      ====================================================== */

      .teacher-section-title {
        padding:
          16px 18px 7px;

        color:
          #8496A5;

        font-size:
          9px;

        font-weight:
          800;

        letter-spacing:
          1.25px;

        user-select:
          none;
      }

      /* ======================================================
         NAVIGATION
      ====================================================== */

      .teacher-nav-item {
        position: relative;

        width:
          calc(100% - 24px);

        min-height:
          43px;

        margin:
          2px 12px;

        padding:
          0 12px;

        border:
          0;

        border-radius:
          8px;

        display:
          flex;

        align-items:
          center;

        gap:
          11px;

        color:
          #C7D3DC;

        background:
          transparent;

        cursor:
          pointer;

        text-align:
          left;

        font-family:
          inherit;

        font-size:
          13px;

        font-weight:
          500;

        transition:
          background 150ms ease,
          color 150ms ease,
          transform 150ms ease;
      }

      .teacher-nav-item:hover {
        background:
          linear-gradient(
            90deg,
            rgba(10,42,67,.95),
            rgba(10,42,67,.42)
          );

        color:
          #FFFFFF;
      }

      .teacher-nav-item:hover
      .teacher-nav-icon {
        color:
          #F6C944;

        border-color:
          rgba(246,201,68,.22);

        background:
          rgba(246,201,68,.08);
      }

      .teacher-nav-item.active {
        background:
          linear-gradient(
            90deg,
            rgba(10,42,67,.98),
            rgba(18,63,99,.78),
            rgba(5,11,18,.30)
          );

        color:
          #FFFFFF;

        font-weight:
          700;

        box-shadow:
          0 6px 18px
          rgba(0,0,0,.24);
      }

      .teacher-nav-item.active::before {
        content:
          "";

        position:
          absolute;

        left:
          0;

        top:
          7px;

        bottom:
          7px;

        width:
          3px;

        border-radius:
          0 3px 3px 0;

        background:
          linear-gradient(
            180deg,
            #F6C944,
            #D4A017
          );

        box-shadow:
          0 0 10px
          rgba(246,201,68,.55);
      }

      .teacher-nav-item.active
      .teacher-nav-icon {
        color:
          #F6C944;

        border-color:
          rgba(246,201,68,.28);

        background:
          rgba(246,201,68,.10);
      }

      .teacher-nav-icon {
        width:
          28px;

        height:
          28px;

        min-width:
          28px;

        display:
          flex;

        align-items:
          center;

        justify-content:
          center;

        border-radius:
          7px;

        color:
          #9EB2C1;

        border:
          1px solid transparent;

        font-size:
          15px;

        transition:
          all 150ms ease;
      }

      .teacher-nav-label {
        overflow:
          hidden;

        white-space:
          nowrap;

        text-overflow:
          ellipsis;

        line-height:
          1.2;
      }

      .teacher-nav-chevron {
        margin-left:
          auto;

        opacity:
          0;

        transform:
          translateX(-3px);

        transition:
          all 150ms ease;

        color:
          #F6C944;
      }

      .teacher-nav-item:hover
      .teacher-nav-chevron,
      .teacher-nav-item.active
      .teacher-nav-chevron {
        opacity:
          1;

        transform:
          translateX(0);
      }

      .teacher-collapsed-item {
        width:
          calc(100% - 18px);

        justify-content:
          center;

        margin-left:
          9px;

        margin-right:
          9px;

        padding:
          0;
      }

      .teacher-collapsed-item
      .teacher-nav-chevron {
        display:
          none;
      }

      .teacher-collapsed-item
      .teacher-nav-label {
        display:
          none;
      }

      .teacher-collapsed-item
      .teacher-nav-icon {
        width:
          34px;

        height:
          34px;

        min-width:
          34px;
      }

      /* ======================================================
         PROFILE
      ====================================================== */

      .teacher-profile-container {
        width:
          40px;

        height:
          40px;

        min-width:
          40px;

        border-radius:
          50%;

        overflow:
          hidden;

        display:
          flex;

        align-items:
          center;

        justify-content:
          center;

        border:
          2px solid #F6C944;

        background:
          #FFFFFF;

        box-shadow:
          0 3px 12px
          rgba(5,11,18,.18);

        position:
          relative;

        flex-shrink:
          0;
      }

      .teacher-profile-avatar {
        width:
          100%;

        height:
          100%;

        display:
          flex;

        align-items:
          center;

        justify-content:
          center;

        overflow:
          hidden;

        border-radius:
          50%;
      }

      .teacher-profile-container > *,
      .teacher-profile-avatar > * {
        width:
          100% !important;

        height:
          100% !important;

        min-width:
          0 !important;

        min-height:
          0 !important;

        margin:
          0 !important;

        padding:
          0 !important;

        display:
          flex !important;

        align-items:
          center !important;

        justify-content:
          center !important;
      }

      .teacher-profile-container img,
      .teacher-profile-avatar img {
        width:
          100% !important;

        height:
          100% !important;

        min-width:
          100% !important;

        min-height:
          100% !important;

        object-fit:
          cover !important;

        object-position:
          center !important;

        display:
          block !important;

        margin:
          0 !important;

        padding:
          0 !important;
      }

      /* ======================================================
         WORKSPACE
      ====================================================== */

      .teacher-workspace {
        border:
          1px solid #D9E0E7;

        background:
          #FFFFFF;

        border-radius:
          11px;

        min-height:
          calc(100vh - 111px);

        box-shadow:
          0 4px 18px
          rgba(5,11,18,.045);

        position:
          relative;

        overflow:
          hidden;
      }

      .teacher-workspace::before {
        content:
          "";

        position:
          absolute;

        top:
          0;

        left:
          0;

        right:
          0;

        height:
          3px;

        background:
          linear-gradient(
            90deg,
            #071D2F 0%,
            #0A2A43 45%,
            #F6C944 100%
          );

        opacity:
          .9;

        pointer-events:
          none;
      }

      .teacher-workspace-dark {
        background:
          #101D29;

        border-color:
          #263B4C;
      }

      /* ======================================================
         PAGE HEADING
      ====================================================== */

      .teacher-page-heading {
        min-height:
          34px;

        display:
          flex;

        align-items:
          center;

        justify-content:
          space-between;

        gap:
          12px;

        margin-bottom:
          8px;

        padding:
          0 2px;
      }

      .teacher-page-heading-left {
        min-width:
          0;

        display:
          flex;

        align-items:
          center;

        gap:
          8px;
      }

      .teacher-page-heading-line {
        width:
          4px;

        height:
          18px;

        min-width:
          4px;

        border-radius:
          3px;

        background:
          linear-gradient(
            180deg,
            #0A2A43,
            #F6C944
          );

        box-shadow:
          0 0 7px
          rgba(10,42,67,.20);
      }

      .teacher-page-heading-title {
        overflow:
          hidden;

        text-overflow:
          ellipsis;

        white-space:
          nowrap;

        color:
          #111827;

        font-size:
          14px;

        font-weight:
          750;
      }

      .teacher-dark
      .teacher-page-heading-title {
        color:
          #F3F8FA;
      }

      .teacher-page-heading-meta {
        color:
          #667085;

        font-size:
          10px;

        font-weight:
          600;

        white-space:
          nowrap;

        letter-spacing:
          .2px;
      }

      .teacher-dark
      .teacher-page-heading-meta {
        color:
          #9FB0BD;
      }

      /* ======================================================
         SIDEBAR
      ====================================================== */

      .teacher-sidebar-shell {
        position:
          relative;

        height:
          100vh;

        min-height:
          100vh;

        overflow:
          hidden;

        background:
          radial-gradient(
            circle at 18% 10%,
            rgba(246,201,68,.07),
            transparent 23%
          ),
          radial-gradient(
            circle at 88% 82%,
            rgba(29,95,140,.13),
            transparent 27%
          ),
          linear-gradient(
            145deg,
            #050B12 0%,
            #071523 48%,
            #03070C 100%
          );
      }

      .teacher-sidebar-shell::after {
        content:
          "";

        position:
          absolute;

        top:
          0;

        right:
          0;

        bottom:
          0;

        width:
          1px;

        pointer-events:
          none;

        background:
          linear-gradient(
            180deg,
            rgba(246,201,68,.08),
            rgba(246,201,68,.32),
            rgba(29,95,140,.24),
            rgba(246,201,68,.06)
          );

        box-shadow:
          0 0 18px
          rgba(246,201,68,.08);
      }

      .teacher-sidebar-content {
        position:
          relative;

        z-index:
          2;

        height:
          100%;

        min-height:
          100vh;

        display:
          flex;

        flex-direction:
          column;

        overflow:
          hidden;
      }

      /* ======================================================
         NAVIGATION PROGRESS
      ====================================================== */

      .teacher-navigation-progress {
        position:
          absolute;

        top:
          0;

        left:
          0;

        right:
          0;

        height:
          3px;

        z-index:
          1000;

        overflow:
          hidden;

        background:
          rgba(10,42,67,.12);
      }

      .teacher-navigation-progress::after {
        content:
          "";

        position:
          absolute;

        top:
          0;

        left:
          0;

        width:
          0;

        height:
          100%;

        background:
          linear-gradient(
            90deg,
            #071D2F,
            #0A2A43,
            #F6C944
          );

        box-shadow:
          0 0 8px
          rgba(246,201,68,.40);

        animation:
          teacherNavigationProgress
          .7s linear forwards;
      }

      @keyframes teacherNavigationProgress {
        0% {
          width:
            0%;
        }

        20% {
          width:
            28%;
        }

        50% {
          width:
            60%;
        }

        80% {
          width:
            85%;
        }

        100% {
          width:
            100%;
        }
      }

      .teacher-loading-label {
        position:
          absolute;

        top:
          11px;

        left:
          50%;

        transform:
          translateX(-50%);

        z-index:
          1001;

        padding:
          5px 10px;

        border-radius:
          6px;

        background:
          linear-gradient(
            135deg,
            rgba(4,19,31,.98),
            rgba(10,42,67,.98)
          );

        color:
          #FFFFFF;

        font-size:
          9px;

        font-weight:
          700;

        letter-spacing:
          .75px;

        text-transform:
          uppercase;

        white-space:
          nowrap;

        box-shadow:
          0 5px 15px
          rgba(0,0,0,.22);
      }

      /* ======================================================
         MOBILE
      ====================================================== */

      .teacher-mobile-drawer
      .ant-drawer-body {
        padding:
          0 !important;
      }

      .teacher-mobile-drawer
      .ant-drawer-content {
        background:
          #050B12 !important;
      }

      .teacher-mobile-drawer
      .ant-drawer-wrapper-body {
        overflow:
          hidden;
      }

      /* ======================================================
         DASHBOARD CARDS
      ====================================================== */

      .teacher-action-card {
        transition:
          transform .22s ease,
          box-shadow .22s ease,
          border-color .22s ease;
      }

      .teacher-action-card:hover {
        transform:
          translateY(-4px);
      }

      .teacher-dashboard-card {
        transition:
          box-shadow .22s ease,
          transform .22s ease;
      }

      .teacher-dashboard-card:hover {
        transform:
          translateY(-2px);
      }

      /* ======================================================
         MOBILE
      ====================================================== */

      @media (max-width: 899px) {
        .teacher-header-actions {
          gap:
            6px;
        }

        .teacher-profile-container {
          width:
            36px;

          height:
            36px;

          min-width:
            36px;
        }

        .teacher-workspace {
          min-height:
            calc(100vh - 96px);

          border-radius:
            8px;
        }

        .teacher-page-heading {
          min-height:
            30px;
        }

        .teacher-page-heading-title {
          font-size:
            13px;
        }

        .teacher-page-heading-meta {
          display:
            none;
        }
      }

      @media (max-width: 650px) {
        .teacher-header-actions {
          gap:
            5px;
        }

        .teacher-header-action {
          width:
            36px;

          height:
            36px;

          min-width:
            36px;
        }

        .teacher-profile-container {
          width:
            34px;

          height:
            34px;

          min-width:
            34px;
        }

        .teacher-menu-button {
          width:
            36px !important;

          height:
            36px !important;

          min-width:
            36px !important;
        }
      }

      @media (max-width: 480px) {
        .teacher-header-actions {
          gap:
            4px;
        }

        .teacher-header-action {
          width:
            34px;

          height:
            34px;

          min-width:
            34px;
        }

        .teacher-profile-container {
          width:
            32px;

          height:
            32px;

          min-width:
            32px;
        }
      }
    `;

    document.head.appendChild(style);

    return () => {
      const existing =
        document.getElementById(
          "teacher-official-dashboard-styles"
        );

      if (existing) {
        existing.remove();
      }
    };
  }, []);

  /* ==========================================================
     THEME
  ========================================================== */

  const theme = useMemo(
    () => ({
      darkMode,

      background:
        darkMode
          ? "#07131E"
          : COLORS.page,

      sidebar:
        COLORS.sidebar,

      header:
        darkMode
          ? "#0D1B27"
          : COLORS.pureWhite,

      card:
        darkMode
          ? "#101D29"
          : COLORS.pureWhite,

      elevatedCard:
        darkMode
          ? "#172633"
          : COLORS.pureWhite,

      inputBackground:
        darkMode
          ? "#101D29"
          : COLORS.pureWhite,

      text:
        darkMode
          ? "#F3F8FA"
          : COLORS.text,

      secondary:
        darkMode
          ? "#9FB0BD"
          : COLORS.muted,

      muted:
        darkMode
          ? "#718697"
          : COLORS.gray,

      border:
        darkMode
          ? "#263B4C"
          : COLORS.border,

      primary:
        COLORS.primary,

      primaryLight:
        darkMode
          ? "#102A42"
          : "#FFF9E6",

      blue:
        COLORS.secondary,

      cyan:
        "#1D5F8C",

      indigo:
        "#315B7A",

      violet:
        "#6A4C93",

      green:
        COLORS.green,

      emerald:
        COLORS.emerald,

      orange:
        COLORS.orange,

      amber:
        COLORS.warning,

      red:
        COLORS.error,

      gold:
        COLORS.gold,

      yellow:
        COLORS.yellow,

      navy:
        COLORS.primaryDark,

      sky:
        COLORS.sky,

      antique:
        COLORS.antique,

      black:
        COLORS.black,

      white:
        COLORS.white,
    }),
    [darkMode]
  );

  /* ==========================================================
     MENU LOOKUP
  ========================================================== */

  const menuLookup = useMemo(
    () =>
      Object.fromEntries(
        menuItems.map((item) => [
          item.key,
          item,
        ])
      ),
    []
  );

  /* ==========================================================
     SIDEBAR SEARCH
  ========================================================== */

  const filteredSections = useMemo(() => {
    const query =
      menuSearch
        .trim()
        .toLowerCase();

    if (!query) {
      return navigationSections;
    }

    return navigationSections
      .map((section) => ({
        ...section,

        items:
          section.items.filter(
            (key) =>
              menuLookup[key]
                ?.label
                ?.toLowerCase()
                .includes(query)
          ),
      }))
      .filter(
        (section) =>
          section.items.length > 0
      );
  }, [
    menuSearch,
    menuLookup,
  ]);

  /* ==========================================================
     ACTIVE TITLE
  ========================================================== */

  const activeTitle =
    menuLookup[activePage]
      ?.label ||
    "Teacher Dashboard";

  /* ==========================================================
     PAGE RENDERING
  ========================================================== */

  const renderPage = () => {
    switch (activePage) {
      case "gradedistribution":
        return (
          <GradeDistribution
            theme={theme}
          />
        );

      case "curriculumteacherworkspace":
        return (
          <CurriculumTeacherWorkspace
            theme={theme}
          />
        );

      case "viewcurriculums":
        return (
          <ViewCurriculums
            theme={theme}
          />
        );

      case "curriculumtopicdetails":
        return (
          <CurriculumTopicDetails
            theme={theme}
          />
        );

      case "curriculumlessoneditor":
        return (
          <CurriculumLessonEditor
            theme={theme}
          />
        );

      case "lessonplan":
        return (
          <LessonPlan
            theme={theme}
          />
        );

      case "viewlessonplan":
        return (
          <ViewLessonPlan
            theme={theme}
          />
        );

      case "teacherassignments":
        return (
          <PostAssignment
            theme={theme}
          />
        );

      case "assignmentview":
        return (
          <AssignmentView
            theme={theme}
          />
        );

      case "test":
        return (
          <Test
            theme={theme}
          />
        );

      case "teachertests":
        return (
          <TestPraint
            theme={theme}
          />
        );

      case "complaint":
        return (
          <Complaint
            theme={theme}
          />
        );

      case "attendancedata":
        return (
          <AttendanceData
            theme={theme}
          />
        );

      case "attendance":
        return (
          <AttendanceRecord
            theme={theme}
          />
        );

      case "quicklinks":
      default:
        return renderDashboardHome();
    }
  };

  /* ==========================================================
     NAVIGATION
  ========================================================== */

  const handleNavigation = (key) => {
    if (isNavigating) {
      return;
    }

    if (key === activePage) {
      if (isMobile) {
        setDrawerOpen(false);
      }

      return;
    }

    setPendingPage(key);
    setIsNavigating(true);
    setMenuSearch("");

    if (isMobile) {
      setDrawerOpen(false);
    }

    window.setTimeout(() => {
      setActivePage(key);
      setPendingPage(null);
      setIsNavigating(false);
    }, 700);
  };

  /* ==========================================================
     NAVIGATION ITEM
  ========================================================== */

  const renderNavigationItem = (
    item
  ) => {
    const active =
      activePage === item.key;

    return (
      <Tooltip
        key={item.key}
        title={
          collapsed
            ? item.label
            : ""
        }
        placement="right"
      >
        <button
          type="button"
          className={[
            "teacher-nav-item",
            active
              ? "active"
              : "",
            collapsed
              ? "teacher-collapsed-item"
              : "",
          ].join(" ")}
          onClick={() =>
            handleNavigation(
              item.key
            )
          }
          disabled={
            isNavigating
          }
          aria-current={
            active
              ? "page"
              : undefined
          }
        >
          <span className="teacher-nav-icon">
            {item.icon}
          </span>

          <span className="teacher-nav-label">
            {item.label}
          </span>

          <span className="teacher-nav-chevron">
            <span
              style={{
                display:
                  "flex",
                fontSize:
                  9,
              }}
            >
              ›
            </span>
          </span>
        </button>
      </Tooltip>
    );
  };

  /* ==========================================================
     SIDEBAR
  ========================================================== */

  const sidebar = (
    <div className="teacher-sidebar-shell">
      <div className="teacher-sidebar-content">
        <div
          style={{
            height:
              "100%",
            minHeight:
              "100vh",
            display:
              "flex",
            flexDirection:
              "column",
            background:
              "transparent",
          }}
        >
          {/* ==================================================
              BRAND HEADER
          ================================================== */}

          <div
            style={{
              height:
                72,

              minHeight:
                72,

              display:
                "flex",

              alignItems:
                "center",

              justifyContent:
                collapsed
                  ? "center"
                  : "flex-start",

              gap:
                11,

              padding:
                collapsed
                  ? "0 10px"
                  : "0 18px",

              borderBottom:
                "1px solid rgba(246,201,68,.14)",

              flexShrink:
                0,
            }}
          >
            <div
              style={{
                width:
                  38,

                height:
                  38,

                minWidth:
                  38,

                borderRadius:
                  8,

                padding:
                  2,

                background:
                  "#FFFFFF",

                display:
                  "flex",

                alignItems:
                  "center",

                justifyContent:
                  "center",

                overflow:
                  "hidden",

                border:
                  "1px solid rgba(246,201,68,.45)",

                boxShadow:
                  "0 4px 14px rgba(0,0,0,.25)",
              }}
            >
              <img
                src={logo}
                alt="Exxellent Kids Foundation logo"
                style={{
                  width:
                    "100%",

                  height:
                    "100%",

                  objectFit:
                    "cover",

                  borderRadius:
                    6,

                  display:
                    "block",
                }}
              />
            </div>

            {!collapsed && (
              <div
                style={{
                  minWidth:
                    0,
                }}
              >
                <div
                  style={{
                    color:
                      "#F4F9FB",

                    fontSize:
                      13,

                    fontWeight:
                      800,

                    whiteSpace:
                      "nowrap",

                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",

                    letterSpacing:
                      ".15px",
                  }}
                >
                  ZION PRAISE ACADEMY
                </div>

                <div
                  style={{
                    marginTop:
                      3,

                    color:
                      "#F6C944",

                    fontSize:
                      8,

                    fontWeight:
                      800,

                    letterSpacing:
                      1.05,

                    whiteSpace:
                      "nowrap",

                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",
                  }}
                >
                  TEACHER PORTAL
                </div>
              </div>
            )}
          </div>

          {/* ==================================================
              SIDEBAR SEARCH
          ================================================== */}

          {!collapsed && (
            <div
              className="teacher-sidebar-search"
              style={{
                padding:
                  "13px 12px 6px",

                flexShrink:
                  0,
              }}
            >
              <Input
                allowClear
                value={
                  menuSearch
                }
                onChange={(
                  event
                ) =>
                  setMenuSearch(
                    event.target
                      .value
                  )
                }
                placeholder="Find a module..."
                prefix={
                  <SearchOutlined
                    style={{
                      color:
                        COLORS.gold,
                    }}
                  />
                }
              />
            </div>
          )}

          {/* ==================================================
              NAVIGATION
          ================================================== */}

          <div className="teacher-sidebar-scroll">
            {filteredSections.length ===
              0 && (
              <div
                style={{
                  padding:
                    "35px 20px",

                  textAlign:
                    "center",

                  color:
                    "#718697",

                  fontSize:
                    11,
                }}
              >
                No module found
              </div>
            )}

            {filteredSections.map(
              (section) => {
                const items =
                  section.items
                    .map(
                      (key) =>
                        menuLookup[
                          key
                        ]
                    )
                    .filter(
                      Boolean
                    );

                if (
                  !items.length
                ) {
                  return null;
                }

                return (
                  <div
                    key={
                      section.key
                    }
                  >
                    {!collapsed ? (
                      <div className="teacher-section-title">
                        {
                          section.title
                        }
                      </div>
                    ) : (
                      <div
                        style={{
                          height:
                            12,
                        }}
                      />
                    )}

                    {items.map(
                      renderNavigationItem
                    )}
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );

  /* ==========================================================
     DASHBOARD HOME
  ========================================================== */

  const renderDashboardHome =
    () => {
      const itemsPerPage =
        isMobile
          ? 2
          : isTablet
          ? 3
          : 4;

      const quickLinks = [
        {
          label:
            "Lesson Plan",

          value:
            "lessonplan",

          icon:
            <BookFilled />,

          description:
            "Create and organize your lesson plans",

          color:
            "#0A2A43",

          background:
            "#EDF3F7",
        },

        {
          label:
            "View Plans",

          value:
            "viewlessonplan",

          icon:
            <FileTextOutlined />,

          description:
            "Review and manage your teaching plans",

          color:
            "#315B7A",

          background:
            "#EEF4F8",
        },

        {
          label:
            "Post Assignment",

          value:
            "teacherassignments",

          icon:
            <FaTasks />,

          description:
            "Give students new academic assignments",

          color:
            "#C0750D",

          background:
            "#FFF7E8",
        },

        {
          label:
            "Assignments",

          value:
            "assignmentview",

          icon:
            <FaClipboardList />,

          description:
            "Review and manage student assignments",

          color:
            "#1D5F8C",

          background:
            "#EDF7FB",
        },

        {
          label:
            "Create Test",

          value:
            "test",

          icon:
            <EditOutlined />,

          description:
            "Create classroom tests and assessments",

          color:
            "#198754",

          background:
            "#EFF9F3",
        },

        {
          label:
            "Teacher Tests",

          value:
            "teachertests",

          icon:
            <FaGraduationCap />,

          description:
            "View and manage your prepared tests",

          color:
            "#6A4C93",

          background:
            "#F7F2FB",
        },

        {
          label:
            "Attendance",

          value:
            "attendance",

          icon:
            <CalendarOutlined />,

          description:
            "Record daily student attendance",

          color:
            "#1D5F8C",

          background:
            "#EEF8FC",
        },

        {
          label:
            "Attendance Data",

          value:
            "attendancedata",

          icon:
            <FaChartLine />,

          description:
            "Review attendance records and data",

          color:
            "#C58A00",

          background:
            "#FFF9E8",
        },

        {
          label:
            "Complaints",

          value:
            "complaint",

          icon:
            <MessageOutlined />,

          description:
            "Submit and manage your complaints",

          color:
            "#B42318",

          background:
            "#FEF1F0",
        },
      ];

      const maxIndex =
        Math.max(
          quickLinks.length -
            itemsPerPage,
          0
        );

      const visibleItems =
        quickLinks.slice(
          visibleIndex,
          visibleIndex +
            itemsPerPage
        );

      return (
        <div
          style={{
            width:
              "100%",

            maxWidth:
              1800,

            margin:
              "0 auto",
          }}
        >
          {/* ==================================================
              WELCOME CARD
          ================================================== */}

          <motion.div
            initial={{
              opacity:
                0,

              y:
                8,
            }}
            animate={{
              opacity:
                1,

              y:
                0,
            }}
            transition={{
              duration:
                0.25,
            }}
          >
            <Card
              bordered
              className="teacher-dashboard-card"
              style={{
                marginBottom:
                  20,

                borderRadius:
                  12,

                borderColor:
                  theme.border,

                overflow:
                  "hidden",

                background:
                  theme.card,

                boxShadow:
                  darkMode
                    ? "0 12px 35px rgba(0,0,0,.20)"
                    : "0 10px 30px rgba(15,23,42,.055)",
              }}
              bodyStyle={{
                padding:
                  0,
              }}
            >
              <div
                style={{
                  position:
                    "relative",

                  overflow:
                    "hidden",

                  padding:
                    isMobile
                      ? "20px 17px"
                      : isTablet
                      ? "25px 25px"
                      : "28px 30px",

                  background:
                    darkMode
                      ? "linear-gradient(135deg,#101D29 0%,#13283A 55%,#102A42 100%)"
                      : "linear-gradient(135deg,#FFFFFF 0%,#F7FAFC 55%,#F0F5F8 100%)",
                }}
              >
                <div
                  style={{
                    position:
                      "absolute",

                    width:
                      300,

                    height:
                      300,

                    borderRadius:
                      "50%",

                    right:
                      -135,

                    top:
                      -175,

                    background:
                      "linear-gradient(135deg,#0A2A43,#F6C944)",

                    opacity:
                      darkMode
                        ? .15
                        : .08,
                  }}
                />

                <div
                  style={{
                    position:
                      "absolute",

                    width:
                      190,

                    height:
                      190,

                    borderRadius:
                      "50%",

                    left:
                      -95,

                    bottom:
                      -130,

                    background:
                      "linear-gradient(135deg,#123F63,#F6C944)",

                    opacity:
                      darkMode
                        ? .10
                        : .05,
                  }}
                />

<Row
  gutter={[24, 24]}
  align="middle"
  style={{
    position: "relative",
    zIndex: 2,
    width: "100%",
  }}
>
  {/* =========================================================
      LEFT — TEACHER IDENTITY & HEADER INFORMATION
  ========================================================= */}
  <Col
    xs={24}
    lg={19}
    xl={20}
  >
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: isMobile
          ? "column"
          : "row",
        alignItems: isMobile
          ? "center"
          : "center",
        justifyContent: isMobile
          ? "center"
          : "flex-start",
        gap: isMobile ? 16 : 22,
        textAlign: isMobile
          ? "center"
          : "left",
      }}
    >
      {/* =====================================================
          TEACHER PROFILE IMAGE
      ===================================================== */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: isMobile
            ? "100%"
            : "auto",
        }}
      >
        <ProfileImageTeacher theme={theme} />
      </div>

      {/* =====================================================
          TEACHER HEADER CONTENT
      ===================================================== */}
      <div
        style={{
          minWidth: 0,
          flex: isMobile
            ? "none"
            : 1,
          width: isMobile
            ? "100%"
            : "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: isMobile
            ? "center"
            : "flex-start",
        }}
      >
        {/* Portal Labels */}
        <Space
          wrap
          size={[8, 8]}
          style={{
            marginBottom: 9,
            justifyContent: isMobile
              ? "center"
              : "flex-start",
          }}
        >
          {/* Teacher Portal */}
          <Tag
            icon={<FaChalkboardTeacher />}
            style={{
              margin: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              borderRadius: 999,
              padding: "5px 12px",
              fontWeight: 700,
              fontSize: 10.5,
              lineHeight: 1.4,
              color: darkMode
                ? "#F6C944"
                : "#0A2A43",
              background: darkMode
                ? "rgba(246,201,68,.10)"
                : "#FFF9E6",
              border: darkMode
                ? "1px solid rgba(246,201,68,.25)"
                : "1px solid #E9D68A",
            }}
          >
            Teacher Portal
          </Tag>

          {/* Teaching Workspace */}
          <Tag
            icon={<RocketOutlined />}
            style={{
              margin: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              borderRadius: 999,
              padding: "5px 12px",
              fontWeight: 700,
              fontSize: 10.5,
              lineHeight: 1.4,
              color: darkMode
                ? "#DCEAF3"
                : "#315B7A",
              background: darkMode
                ? "rgba(29,95,140,.18)"
                : "#EEF5F9",
              border: darkMode
                ? "1px solid rgba(29,95,140,.35)"
                : "1px solid #D4E2EA",
            }}
          >
            Teaching Workspace
          </Tag>
        </Space>

        {/* Main Heading */}
        <Title
          level={isMobile ? 3 : 2}
          style={{
            margin: 0,
            color: theme.text,
            fontWeight: 800,
            fontSize: isMobile
              ? 24
              : 31,
            letterSpacing: "-0.7px",
            lineHeight: 1.15,
            textAlign: isMobile
              ? "center"
              : "left",
          }}
        >
          Teacher Dashboard
        </Title>

        {/* Teacher Role Description */}
        <div
          style={{
            marginTop: 7,
            maxWidth: 720,
            color: theme.textSecondary,
            fontSize: isMobile
              ? 12
              : 13,
            lineHeight: 1.6,
            fontWeight: 500,
            textAlign: isMobile
              ? "center"
              : "left",
          }}
        >
          Manage classes, teach courses, record attendance,
          prepare assessments, enter results, and support
          student learning and academic progress.
        </div>

        {/* =================================================
            TEACHER FUNCTIONS
        ================================================= */}
        <Space
          wrap
          size={[6, 6]}
          style={{
            marginTop: 11,
            justifyContent: isMobile
              ? "center"
              : "flex-start",
            width: isMobile
              ? "100%"
              : "auto",
          }}
        >
          {/* Classes & Courses */}
          <Tag
            icon={<FaBookOpen />}
            style={{
              margin: 0,
              borderRadius: 7,
              fontSize: 10.5,
              fontWeight: 600,
              padding: "4px 9px",
            }}
          >
            Classes & Courses
          </Tag>

          {/* Assessments */}
          <Tag
            icon={<FaClipboardCheck />}
            style={{
              margin: 0,
              borderRadius: 7,
              fontSize: 10.5,
              fontWeight: 600,
              padding: "4px 9px",
            }}
          >
            Assessments
          </Tag>

          {/* Attendance */}
          <Tag
            icon={<FaUserCheck />}
            style={{
              margin: 0,
              borderRadius: 7,
              fontSize: 10.5,
              fontWeight: 600,
              padding: "4px 9px",
            }}
          >
            Attendance
          </Tag>

          {/* Student Results */}
          <Tag
            icon={<FaChartLine />}
            style={{
              margin: 0,
              borderRadius: 7,
              fontSize: 10.5,
              fontWeight: 600,
              padding: "4px 9px",
            }}
          >
            Student Results
          </Tag>
        </Space>
      </div>
    </div>
  </Col>

  {/* =========================================================
      RIGHT — TEACHER PORTAL IDENTITY
  ========================================================= */}
  <Col
    xs={24}
    lg={5}
    xl={4}
  >
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: isMobile
          ? "center"
          : "flex-end",
        alignItems: "center",
        marginTop: isMobile
          ? 2
          : 0,
      }}
    >
      <div
        style={{
          width: isMobile
            ? 68
            : 86,
          height: isMobile
            ? 68
            : 86,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 20,
          background:
            "linear-gradient(145deg, #0A2A43 0%, #123F63 100%)",
          border:
            "1px solid rgba(246,201,68,.38)",
          boxShadow: darkMode
            ? "0 14px 32px rgba(0,0,0,.28)"
            : "0 14px 32px rgba(5,11,18,.16)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Glow */}
        <div
          style={{
            position: "absolute",
            width: isMobile
              ? 42
              : 54,
            height: isMobile
              ? 42
              : 54,
            borderRadius: "50%",
            background:
              "rgba(246,201,68,.08)",
            top: -12,
            right: -12,
          }}
        />

        {/* Teacher Identity Icon */}
        <FaChalkboardTeacher
          size={isMobile ? 29 : 37}
          color="#F6C944"
          style={{
            position: "relative",
            zIndex: 1,
          }}
        />
      </div>
    </div>
  </Col>
</Row>
              </div>
            </Card>
          </motion.div>

          {/* ==================================================
              QUICK ACTIONS
          ================================================== */}

          <div
            style={{
              display:
                "flex",

              alignItems:
                "center",

              justifyContent:
                "space-between",

              gap:
                12,

              marginBottom:
                13,
            }}
          >
            <div
              style={{
                minWidth:
                  0,
              }}
            >
              <Title
                level={4}
                style={{
                  margin:
                    0,

                  color:
                    theme.text,

                  fontSize:
                    isMobile
                      ? 18
                      : 20,

                  fontWeight:
                    750,
                }}
              >
                Quick Actions
              </Title>

              <Text
                style={{
                  color:
                    theme.secondary,

                  fontSize:
                    12,
                }}
              >
                Frequently used teaching
                tools
              </Text>
            </div>

            <Space size={6}>
              <Tooltip title="Previous">
                <Button
                  className="teacher-header-button"
                  shape="circle"
                  icon={
                    <ArrowLeftOutlined />
                  }
                  disabled={
                    visibleIndex ===
                    0
                  }
                  onClick={() =>
                    setVisibleIndex(
                      (prev) =>
                        Math.max(
                          prev -
                            itemsPerPage,
                          0
                        )
                    )
                  }
                  style={{
                    borderColor:
                      theme.border,
                    color:
                      theme.text,
                    background:
                      theme.card,
                  }}
                />
              </Tooltip>

              <Tooltip title="Next">
                <Button
                  className="teacher-header-button"
                  shape="circle"
                  icon={
                    <ArrowRightOutlined />
                  }
                  disabled={
                    visibleIndex >=
                    maxIndex
                  }
                  onClick={() =>
                    setVisibleIndex(
                      (prev) =>
                        Math.min(
                          prev +
                            itemsPerPage,
                          maxIndex
                        )
                    )
                  }
                  style={{
                    borderColor:
                      theme.border,
                    color:
                      theme.text,
                    background:
                      theme.card,
                  }}
                />
              </Tooltip>
            </Space>
          </div>

          <Row
            gutter={[
              14,
              14,
            ]}
            style={{
              marginBottom:
                22,
            }}
          >
            {visibleItems.map(
              (
                item,
                index
              ) => (
                <Col
                  xs={12}
                  sm={12}
                  md={
                    isTablet
                      ? 8
                      : 6
                  }
                  lg={6}
                  key={
                    item.value
                  }
                >
                  <motion.div
                    initial={{
                      opacity:
                        0,

                      y:
                        8,
                    }}
                    animate={{
                      opacity:
                        1,

                      y:
                        0,
                    }}
                    transition={{
                      duration:
                        .22,

                      delay:
                        index *
                        .035,
                    }}
                    style={{
                      height:
                        "100%",
                    }}
                  >
                    <Card
                      hoverable
                      onClick={() =>
                        setActivePage(
                          item.value
                        )
                      }
                      className="teacher-action-card"
                      style={{
                        height:
                          "100%",

                        minHeight:
                          isMobile
                            ? 145
                            : 160,

                        borderRadius:
                          11,

                        borderColor:
                          theme.border,

                        background:
                          theme.card,

                        boxShadow:
                          darkMode
                            ? "0 8px 25px rgba(0,0,0,.14)"
                            : "0 7px 22px rgba(15,23,42,.045)",

                        cursor:
                          "pointer",
                      }}
                      bodyStyle={{
                        padding:
                          isMobile
                            ? 14
                            : 17,

                        height:
                          "100%",
                      }}
                    >
                      <div
                        style={{
                          display:
                            "flex",

                          flexDirection:
                            "column",

                          height:
                            "100%",
                        }}
                      >
                        <div
                          style={{
                            display:
                              "flex",

                            alignItems:
                              "center",

                            justifyContent:
                              "space-between",

                            marginBottom:
                              13,
                          }}
                        >
                          <div
                            style={{
                              width:
                                isMobile
                                  ? 41
                                  : 46,

                              height:
                                isMobile
                                  ? 41
                                  : 46,

                              borderRadius:
                                11,

                              display:
                                "flex",

                              alignItems:
                                "center",

                              justifyContent:
                                "center",

                              color:
                                item.color,

                              background:
                                item.background,

                              fontSize:
                                18,
                            }}
                          >
                            {
                              item.icon
                            }
                          </div>

                          <ArrowRightOutlined
                            style={{
                              color:
                                theme.muted,

                              fontSize:
                                11,
                            }}
                          />
                        </div>

                        <Text
                          strong
                          style={{
                            color:
                              theme.text,

                            fontSize:
                              isMobile
                                ? 12.5
                                : 13.5,

                            marginBottom:
                              5,

                            lineHeight:
                              1.35,
                          }}
                        >
                          {
                            item.label
                          }
                        </Text>

                        <Text
                          style={{
                            color:
                              theme.secondary,

                            fontSize:
                              11,

                            lineHeight:
                              1.5,
                          }}
                        >
                          {
                            item.description
                          }
                        </Text>
                      </div>
                    </Card>
                  </motion.div>
                </Col>
              )
            )}
          </Row>

          {/* ==================================================
              SUPPORTING TOOLS
          ================================================== */}

          <Row
            gutter={[
              16,
              16,
            ]}
            style={{
              marginBottom:
                25,
            }}
          >
            <Col
              xs={24}
              md={8}
            >
              <Card
                className="teacher-dashboard-card"
                bordered
                style={{
                  height:
                    "100%",

                  borderRadius:
                    11,

                  borderColor:
                    theme.border,

                  background:
                    theme.card,
                }}
                bodyStyle={{
                  padding:
                    isMobile
                      ? 16
                      : 18,
                }}
              >
                <Space
                  align="start"
                  size={11}
                  style={{
                    marginBottom:
                      17,
                  }}
                >
                  <Avatar
                    shape="square"
                    size={44}
                    icon={
                      <FileAddOutlined />
                    }
                    style={{
                      background:
                        darkMode
                          ? "#102A42"
                          : "#EEF4F8",

                      color:
                        "#0A2A43",
                    }}
                  />

                  <div>
                    <Text
                      strong
                      style={{
                        display:
                          "block",

                        color:
                          theme.text,

                        fontSize:
                          14,
                      }}
                    >
                      Teaching Resources
                    </Text>

                    <Text
                      style={{
                        color:
                          theme.secondary,

                        fontSize:
                          11.5,
                      }}
                    >
                      Manage classroom
                      materials
                    </Text>
                  </div>
                </Space>

                <Space
                  direction={
                    isMobile
                      ? "vertical"
                      : "horizontal"
                  }
                  style={{
                    width:
                      "100%",
                  }}
                  size={8}
                >
                  <Button
                    block
                    icon={
                      <FileAddOutlined />
                    }
                    onClick={() =>
                      setOpenAttachment(
                        true
                      )
                    }
                  >
                    Test Attachment
                  </Button>

                  <Button
                    block
                    type="primary"
                    icon={
                      <PlusOutlined />
                    }
                    onClick={() =>
                      setOpenNotes(
                        true
                      )
                    }
                    style={{
                      background:
                        "#0A2A43",

                      borderColor:
                        "#0A2A43",
                    }}
                  >
                    Add Notes
                  </Button>
                </Space>
              </Card>
            </Col>

            <Col
              xs={24}
              md={8}
            >
              <Card
                className="teacher-dashboard-card"
                bordered
                style={{
                  height:
                    "100%",

                  borderRadius:
                    11,

                  borderColor:
                    theme.border,

                  background:
                    theme.card,
                }}
                bodyStyle={{
                  padding:
                    isMobile
                      ? 16
                      : 18,
                }}
              >
                <Space
                  align="start"
                  size={11}
                  style={{
                    marginBottom:
                      17,
                  }}
                >
                  <Avatar
                    shape="square"
                    size={44}
                    icon={
                      <MessageOutlined />
                    }
                    style={{
                      background:
                        darkMode
                          ? "#102A42"
                          : "#EDF7FB",

                      color:
                        "#1D5F8C",
                    }}
                  />

                  <div>
                    <Text
                      strong
                      style={{
                        display:
                          "block",

                        color:
                          theme.text,

                        fontSize:
                          14,
                      }}
                    >
                      Communication
                    </Text>

                    <Text
                      style={{
                        color:
                          theme.secondary,

                        fontSize:
                          11.5,
                      }}
                    >
                      Stay connected
                      with your school
                    </Text>
                  </div>
                </Space>

                <Link
                  to="/chatApp"
                  style={{
                    textDecoration:
                      "none",
                  }}
                >
                  <Button
                    block
                    type="primary"
                    icon={
                      <MessageOutlined />
                    }
                    style={{
                      height:
                        40,

                      background:
                        "#0A2A43",

                      borderColor:
                        "#0A2A43",
                    }}
                  >
                    Open Teacher Chats
                  </Button>
                </Link>
              </Card>
            </Col>

            <Col
              xs={24}
              md={8}
            >
              <Card
                className="teacher-dashboard-card"
                bordered
                style={{
                  height:
                    "100%",

                  borderRadius:
                    11,

                  borderColor:
                    theme.border,

                  background:
                    theme.card,
                }}
                bodyStyle={{
                  padding:
                    isMobile
                      ? 16
                      : 18,
                }}
              >
                <Space
                  align="start"
                  size={11}
                  style={{
                    marginBottom:
                      17,
                  }}
                >
                  <Avatar
                    shape="square"
                    size={44}
                    icon={
                      <CalendarOutlined />
                    }
                    style={{
                      background:
                        darkMode
                          ? "#102A42"
                          : "#EFF9F3",

                      color:
                        "#198754",
                    }}
                  />

                  <div>
                    <Text
                      strong
                      style={{
                        display:
                          "block",

                        color:
                          theme.text,

                        fontSize:
                          14,
                      }}
                    >
                      Attendance
                    </Text>

                    <Text
                      style={{
                        color:
                          theme.secondary,

                        fontSize:
                          11.5,
                      }}
                    >
                      Manage classroom
                      attendance
                    </Text>
                  </div>
                </Space>

                <Space
                  style={{
                    width:
                      "100%",
                  }}
                  size={8}
                >
                  <Button
                    block
                    onClick={() =>
                      setActivePage(
                        "attendance"
                      )
                    }
                  >
                    Record
                  </Button>

                  <Button
                    block
                    type="primary"
                    onClick={() =>
                      setActivePage(
                        "attendancedata"
                      )
                    }
                    style={{
                      background:
                        "#198754",

                      borderColor:
                        "#198754",
                    }}
                  >
                    View Data
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* ==================================================
              ANALYTICS
          ================================================== */}

          <div
            style={{
              marginBottom:
                13,
            }}
          >
            <Title
              level={4}
              style={{
                margin:
                  0,

                color:
                  theme.text,

                fontSize:
                  isMobile
                    ? 18
                    : 20,

                fontWeight:
                  750,
              }}
            >
              Teaching Overview
            </Title>

            <Text
              style={{
                color:
                  theme.secondary,

                fontSize:
                  12,
              }}
            >
              Academic activity and
              classroom insights
            </Text>
          </div>

          <Card
            bordered
            style={{
              borderRadius:
                11,

              borderColor:
                theme.border,

              background:
                theme.card,

              overflow:
                "hidden",

              marginBottom:
                20,
            }}
            bodyStyle={{
              padding:
                isMobile
                  ? 12
                  : 18,
            }}
          >
            <TeacherAnalytics />
          </Card>
        </div>
      );
    };

  /* ==========================================================
     MAIN RETURN
  ========================================================== */

  return (
    <div
      className={
        darkMode
          ? "teacher-dark"
          : ""
      }
      style={{
        position:
          "fixed",

        inset:
          0,

        zIndex:
          999,

        overflow:
          "hidden",

        background:
          theme.background,
      }}
    >
      <Layout
        style={{
          height:
            "100vh",

          minHeight:
            "100vh",

          background:
            theme.background,
        }}
      >
        {/* ====================================================
            DESKTOP SIDEBAR
        ==================================================== */}

        {!isMobile && (
          <Sider
            width={
              292
            }
            collapsedWidth={
              78
            }
            collapsed={
              collapsed
            }
            trigger={
              null
            }
            style={{
              position:
                "fixed",

              top:
                0,

              bottom:
                0,

              left:
                0,

              height:
                "100vh",

              zIndex:
                100,

              overflow:
                "hidden",

              background:
                COLORS.sidebar,
            }}
          >
            {sidebar}
          </Sider>
        )}

        {/* ====================================================
            MOBILE SIDEBAR
        ==================================================== */}

        {isMobile && (
          <Drawer
            open={
              drawerOpen
            }
            placement="left"
            width={
              310
            }
            closable={
              false
            }
            onClose={() =>
              setDrawerOpen(
                false
              )
            }
            className="teacher-mobile-drawer"
            styles={{
              body: {
                padding:
                  0,

                background:
                  COLORS.sidebar,

                overflow:
                  "hidden",
              },
            }}
          >
            <div
              style={{
                position:
                  "absolute",

                top:
                  13,

                right:
                  13,

                zIndex:
                  20,
              }}
            >
              <Button
                type="text"
                icon={
                  <CloseOutlined />
                }
                onClick={() =>
                  setDrawerOpen(
                    false
                  )
                }
                style={{
                  width:
                    34,

                  height:
                    34,

                  color:
                    "#E8EEF3",

                  background:
                    "rgba(246,201,68,.08)",

                  border:
                    "1px solid rgba(246,201,68,.18)",

                  borderRadius:
                    8,

                  display:
                    "flex",

                  alignItems:
                    "center",

                  justifyContent:
                    "center",
                }}
              />
            </div>

            {sidebar}
          </Drawer>
        )}

        {/* ====================================================
            MAIN LAYOUT
        ==================================================== */}

        <Layout
          style={{
            marginLeft:
              isMobile
                ? 0
                : collapsed
                ? 78
                : 292,

            height:
              "100vh",

            minHeight:
              "100vh",

            background:
              theme.background,

            transition:
              "margin-left 180ms ease",
          }}
        >
          {/* ==================================================
              HEADER
          ================================================== */}

          <Header
            style={{
              height:
                68,

              minHeight:
                68,

              flexShrink:
                0,

              padding:
                isMobile
                  ? "0 9px"
                  : "0 16px",

              display:
                "flex",

              alignItems:
                "center",

              gap:
                isMobile
                  ? 6
                  : 10,

              background:
                theme.header,

              borderBottom:
                `1px solid ${theme.border}`,

              position:
                "relative",

              zIndex:
                30,
            }}
          >
            {isNavigating && (
              <>
                <div className="teacher-navigation-progress" />

                <div className="teacher-loading-label">
                  Opening{" "}
                  {
                    menuLookup[
                      pendingPage
                    ]?.label ||
                    "module"
                  }
                </div>
              </>
            )}

            {/* ==================================================
                MENU BUTTON
            ================================================== */}

            <Button
              type="text"
              className="teacher-menu-button"
              icon={
                isMobile ? (
                  <MenuUnfoldOutlined />
                ) : collapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={() => {
                if (
                  isMobile
                ) {
                  setDrawerOpen(
                    true
                  );
                } else {
                  setCollapsed(
                    (value) =>
                      !value
                  );
                }
              }}
            />

            {/* ==================================================
                HEADER TITLE
            ================================================== */}

            <div
              style={{
                minWidth:
                  0,

                display:
                  "flex",

                flexDirection:
                  "column",

                justifyContent:
                  "center",

                overflow:
                  "hidden",
              }}
            >
              <div
                style={{
                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    8,

                  minWidth:
                    0,
                }}
              >
                <span
                  style={{
                    color:
                      theme.text,

                    fontSize:
                      isMobile
                        ? 14
                        : 15,

                    fontWeight:
                      750,

                    lineHeight:
                      1.2,

                    whiteSpace:
                      "nowrap",

                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",
                  }}
                >
                  {activeTitle}
                </span>
              </div>

              {!isMobile && (
                <span
                  style={{
                    color:
                      theme.secondary,

                    fontSize:
                      10.5,

                    lineHeight:
                      1.4,

                    marginTop:
                      2,

                    whiteSpace:
                      "nowrap",

                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",
                  }}
                >
                  Exxellent Kids Foundation
                  • Teacher Portal
                </span>
              )}
            </div>

            <div
              style={{
                flex:
                  1,

                minWidth:
                  0,
              }}
            />

            {/* ==================================================
                HEADER ACTIONS
            ================================================== */}

            <div className="teacher-header-actions">
              <Darkmode
                darkMode={
                  darkMode
                }
                setDarkMode={
                  setDarkMode
                }
              />

         {!isMobile && (
              <>
                <NotificationIcon
                  theme={theme}
                />

                <Notice />
              </>
            )}

              <Tooltip title="Profile">
                <div className="teacher-profile-container">
                  <div className="teacher-profile-avatar">
                    <ProfileImageTeacher
                      theme={
                        theme
                      }
                      header
                    />
                  </div>
                </div>
              </Tooltip>
            </div>
          </Header>

          {/* ==================================================
              CONTENT
          ================================================== */}

          <Content
            style={{
              background:
                theme.background,

              padding:
                isMobile
                  ? "9px 8px 12px"
                  : "14px 16px 18px",

              minHeight:
                0,

              overflowY:
                "auto",

              overflowX:
                "hidden",
            }}
          >
            {/* ==================================================
                PAGE HEADING
            ================================================== */}

            <div className="teacher-page-heading">
              <div className="teacher-page-heading-left">
                <span className="teacher-page-heading-line" />

                <span className="teacher-page-heading-title">
                  {activeTitle}
                </span>
              </div>

              {!isMobile && (
                <span className="teacher-page-heading-meta">
                  EXXELLENT KIDS FOUNDATION
                  • TEACHER PORTAL
                  • Design by Polu
                </span>
              )}
            </div>

            {/* ==================================================
                WORKSPACE
            ================================================== */}

            <motion.div
              key={
                activePage
              }
              initial={{
                opacity:
                  0,

                y:
                  6,
              }}
              animate={{
                opacity:
                  1,

                y:
                  0,
              }}
              transition={{
                duration:
                  .2,

                ease:
                  "easeOut",
              }}
              className={
                darkMode
                  ? "teacher-workspace teacher-workspace-dark"
                  : "teacher-workspace"
              }
              style={{
                padding:
                  isMobile
                    ? 9
                    : 15,
              }}
            >
              {renderPage()}
            </motion.div>
          </Content>
        </Layout>
      </Layout>

      {/* ======================================================
          TEST ATTACHMENT
      ====================================================== */}

      <TestAttachment
        open={
          openAttachment
        }
        handleClose={() =>
          setOpenAttachment(
            false
          )
        }
      />

      {/* ======================================================
          POST NOTES
      ====================================================== */}

      <PostNotes
        open={
          openNotes
        }
        onClose={() =>
          setOpenNotes(
            false
          )
        }
      />
    </div>
  );
};

export default TeacherDashboard;